import { Component, effect, inject, OnInit } from '@angular/core';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { FileUploadModule } from 'primeng/fileupload';
import { FormFieldWrapperComponent } from '../form-field-wrapper/form-field-wrapper.component';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { NgIf } from '@angular/common';
import { GlobalsStore } from '../../../stores/globals.state';
import { MenuItem } from 'primeng/api';
import {
  Commune,
  leafletTileLayerOptions,
  MapConfiguration,
} from '../../../models/commune';

import { DropdownModule } from 'primeng/dropdown';
import { SliderModule } from 'primeng/slider';
import { NgxSliderComponent } from '../../../ngx-slider/ngx-slider.component';
import { ColorsetDropdownComponent } from '../../atoms/colorset-dropdown/colorset-dropdown.component';
import { RadioButtonModule } from 'primeng/radiobutton';
import { CommuneStore } from '../../../stores/commune/commune.state';
import {
  mapLegendOptions,
  mapLegendPositionOptions,
  mapTooltipStyleDropdownItems,
} from '../../../services/grid-widget-config.service';
import { HeaderComponent } from '../../atoms/header/header.component';
import { DropdownOptionComponent } from '../../atoms/dropdown-option/dropdown-option.component';

@Component({
  selector: 'app-configuration-map',
  standalone: true,
  imports: [
    CheckboxModule,
    DividerModule,
    FileUploadModule,
    FormFieldWrapperComponent,
    FormsModule,
    InputNumberModule,
    InputTextModule,
    NgIf,
    ReactiveFormsModule,
    DropdownModule,
    SliderModule,
    NgxSliderComponent,
    ColorsetDropdownComponent,
    RadioButtonModule,
    HeaderComponent,
    DropdownOptionComponent,
  ],
  templateUrl: './configuration-map.component.html',
  styleUrl: './configuration-map.component.scss',
})
export class ConfigurationMapComponent implements OnInit {
  readonly communeStore = inject(CommuneStore);
  readonly globalStore = inject(GlobalsStore);
  form: FormGroup = new FormGroup({});
  initialized = false;
  items: MenuItem[] = [];
  activeCommune: Commune | null = null;
  mapConfiguration?: MapConfiguration;

  constructor(private fb: FormBuilder) {
    effect(() => {
      this.communeSetup();
    });
  }

  /**
   * Sets up the active commune by ensuring it is properly configured and initializes required configurations.
   * If the active commune is not already determined, it retrieves it from the global store.
   * Ensures that the commune's atlas configuration is established and updates the map configuration accordingly.
   * Finally, initializes the form if the commune is active and has not yet been initialized.
   *
   * @return {void} Does not return a value.
   */
  communeSetup(): void {
    if (!this.activeCommune) {
      this.activeCommune = this.globalStore.activeCommune();
      if (!this.activeCommune) return;
      if (!this.activeCommune.atlasConfig) {
        this.activeCommune.atlasConfig = this.communeStore.baseConfig();
      }
      this.mapConfiguration = this.activeCommune.atlasConfig.map;
    }
    if (this.activeCommune && !this.initialized) {
      this.initForm();
    }
  }

  /**
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * Initializes the component by setting up necessary configurations or data.
   * In this case, it invokes the `communeSetup` method to perform initial setup logic.
   *
   * @return {void} Does not return any value.
   */
  ngOnInit(): void {
    this.communeSetup();
  }

  /**
   * Initializes the form with default or predefined map configuration values.
   * Sets up form controls and their default values based on the current mapConfiguration.
   * Subscribes to form value changes to trigger the submit handler.
   *
   * @return {void} This method does not return a value.
   */
  initForm(): void {
    this.form = this.fb.group({
      tileLayer: [this.mapConfiguration?.tileLayer || ''],
      captions: this.fb.group({
        key: [this.mapConfiguration?.captions?.key || false],
        name: [this.mapConfiguration?.captions?.name || false],
        value: [this.mapConfiguration?.captions?.value || false],
        chart: [this.mapConfiguration?.captions?.chart || false],
        style: [this.mapConfiguration?.captions?.style || 'top'],
      }),
      showChart: [this.mapConfiguration?.showChart || false],
      hideKaValues: [this.mapConfiguration?.hideKaValues || true],
      colorset: [this.mapConfiguration?.colorset],
      direction: [this.mapConfiguration?.direction || 'auto'],
      legend: this.fb.group({
        scaleMode: [this.mapConfiguration?.legend?.scaleMode || 'width'],
        size: [this.mapConfiguration?.legend?.size || 'large'],
        numberOfScales: [this.mapConfiguration?.legend?.numberOfScales || '6'],
        legendScope: [this.mapConfiguration?.legend?.legendScope || 'group'],
        position: [this.mapConfiguration?.legend.position || 'bottomright'],
      }),
      districtOpacity: [this.mapConfiguration?.districtOpacity ?? 1],
      //strokeColor: [this.mapConfiguration?.strokeColor || '#ffffff'],
      poiSize: [this.mapConfiguration?.poiSize || 1],
      emptyFillColor: [this.mapConfiguration?.emptyFillColor || '#efefef'],
    });

    this.form.valueChanges.subscribe(() => {
      this.submit();
    });

    this.initialized = true;
  }

  /**
   * Submits the form data and updates the active commune.
   *
   * This method checks if there is an active commune. If an active commune exists,
   * it merges the form values with the current map configuration of the active commune
   * and updates the active commune's configuration in the commune store.
   *
   * @return {void} Does not return a value.
   */
  submit(): void {
    if (!this.activeCommune) return;

    this.activeCommune.atlasConfig.map = {
      ...this.activeCommune.atlasConfig.map,
      ...this.form.value,
    };
    this.communeStore.update(this.activeCommune);
  }

  protected readonly leafletTileLayerOptions = leafletTileLayerOptions;
  protected readonly mapLegendOptions = mapLegendOptions;
  protected readonly mapLegendPositionOptions = mapLegendPositionOptions;
  protected readonly mapTooltipStyleDropdownItems =
    mapTooltipStyleDropdownItems;
}
